import * as m from '@staiter/i18n';
import type { FieldDefinition, State } from '@staiter/shared/task-module';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Archive,
  ArchiveRestore,
  CircleAlert,
  Globe,
  Lock,
  Mail,
  Trash2,
  UserPlus,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { ArchiveProjectDialog } from '~/components/domain/project/ArchiveProjectDialog';
import { RestoreProjectDialog } from '~/components/domain/project/RestoreProjectDialog';
import { emitToast } from '~/components/feedback/toast-bus';
import { Button, DisabledWithReason } from '~/components/primitives';
import { DuplicateProjectDialog } from '~/components/tasks/blueprints/DuplicateProjectDialog';
import { SaveAsTemplateDialog } from '~/components/tasks/blueprints/SaveAsTemplateDialog';
import { VisibilityChangeDialog } from '~/components/tasks/dialogs/VisibilityChangeDialog';
import { ApiRequestError, apiRequest } from '~/lib/api/client';
import { submitProjectAction } from '~/lib/api/project-actions';
import { changeProjectVisibility, fetchProject } from '~/lib/api/projects';
import { restoreProject } from '~/lib/api/projects';
import { canDo, useUserScope } from '~/lib/rbac/use-user-scope';
import { archiveProject, fetchProjectArchivePreview } from '~/lib/tasks/api/tasks';
import { FIELD_TYPE_LABEL } from '~/lib/tasks/i18n/labels';
import { projectKeys } from '~/lib/tasks/queryKeys';
import styles from './ProjectSettingsView.module.css';
import { StatesManager } from './StatesManager';

/**
 * ProjectSettingsView — настройки проекта (FE-S13-10 + FE-S13-15 compact):
 *   1. Состояния — список с цветом/группой (создание — через канбан «Добавить столбец»).
 *   2. Поля — list FieldDefinitions + создание + soft-delete (30d retention warning).
 *   3. Участники — список + роль + добавление/удаление (через members API).
 */

const FIELD_TYPES = [
  'text',
  'number',
  'decimal',
  'date',
  'checkbox',
  'url',
  'single_select',
] as const;

const MembersResponse = z
  .object({
    members: z.array(
      z.object({
        project_id: z.string(),
        user_id: z.string(),
        role_in_project: z.string(),
        // BE-4 — denorm имени/почты (FE-23: показываем ФИО, не UUID).
        full_name: z.string().nullable().optional(),
        email: z.string().nullable().optional(),
        avatar_url: z.string().nullable().optional(),
        added_at: z.string(),
      }),
    ),
  })
  .transform((d) => d.members);

export type ProjectSettingsViewProps = {
  projectId: string;
  states: readonly State[];
  fieldDefs: readonly FieldDefinition[];
  users: ReadonlyArray<{ id: string; name: string }>;
  /**
   * CM-F3 (D4.11a) — раздел-якорь из ссылки меню. Пункты «Участники» /
   * «Видимость» / «Архивировать» живут ЗДЕСЬ, и раньше меню приводило на общую
   * страницу настроек: дальше нужный блок искали глазами.
   */
  section?: ProjectSettingsSection;
};

/** CM-F3 — адресуемые разделы настроек. Ключ = якорь в URL и `id` секции. */
export const PROJECT_SETTINGS_SECTIONS = [
  'states',
  'fields',
  'visibility',
  'members',
  'template',
  'archive',
] as const;
export type ProjectSettingsSection = (typeof PROJECT_SETTINGS_SECTIONS)[number];

export function ProjectSettingsView({
  projectId,
  states,
  fieldDefs,
  users,
  section,
}: ProjectSettingsViewProps): JSX.Element {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const ROLE_LABEL: { [role: string]: string } = {
    lead: m.tm_lead(),
    member: m.tm_member(),
    viewer: m.tm_watcher(),
  };
  const [newField, setNewField] = useState({ key: '', label: '', type: 'text' as string });
  // FB15 — confirm архивации с preview (BB11).
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  // F4.1 — вторая точка входа save/duplicate (для живущих в /work-tasks workspace).
  const [templateDialog, setTemplateDialog] = useState<'save' | 'duplicate' | null>(null);
  const projectQuery = useQuery({
    queryKey: projectKeys.detail(projectId),
    queryFn: () => fetchProject(projectId),
  });
  const projectName = projectQuery.data?.name ?? '';
  const scope = useUserScope();
  const canEditProject = canDo(scope, 'project', 'update');
  // F7 — архивация идёт под `delete`, как и отмена: одно право на терминальные решения.
  const canDeleteProject = canDo(scope, 'project', 'delete');
  /** F9 — архивный проект достижим прямой ссылкой; из него нужен выход. */
  const isArchived = projectQuery.data?.archived_at != null;
  const [restoreOpen, setRestoreOpen] = useState(false);
  /**
   * F1 (tier2-approval-requests §5) — возврат из архива Tier 2 уходит ЗАЯВКОЙ.
   *
   * Прежде отсюда уезжала выдуманная подпись, и человек считал проект возвращённым.
   * Теперь возврат произойдёт после решения названного адресата.
   */
  const restoreMutation = useMutation({
    mutationFn: (payload: { reason: string; addressee_user_id?: string }) =>
      submitProjectAction({
        projectId,
        taskType: 'project_restore',
        reason: payload.reason,
        addresseeUserId: payload.addressee_user_id,
        direct: () => restoreProject(projectId, { reason: payload.reason }),
      }),
    onSuccess: (res) => {
      setRestoreOpen(false);
      emitToast(
        res.mode === 'requested'
          ? {
              variant: 'info',
              title: m.approval_request_sent_title(),
              description: m.approval_request_sent_body(),
              durationMs: 6000,
            }
          : {
              variant: 'success',
              title: m.project_restore_title(),
              durationMs: 5000,
            },
      );
      qc.invalidateQueries({ queryKey: projectKeys.all });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      void res;
    },
  });

  const membersQuery = useQuery({
    queryKey: [...projectKeys.detail(projectId), 'members'],
    queryFn: () => apiRequest(`/task-module/projects/${projectId}/members`, MembersResponse),
  });
  const members = membersQuery.data ?? [];

  const archivePreviewQuery = useQuery({
    queryKey: [...projectKeys.detail(projectId), 'archive-preview'],
    queryFn: () => fetchProjectArchivePreview(projectId),
    enabled: archiveConfirmOpen,
  });
  const archiveMutation = useMutation({
    mutationFn: (payload: { reason: string; addressee_user_id?: string }) =>
      submitProjectAction({
        projectId,
        taskType: 'project_archive',
        reason: payload.reason,
        addresseeUserId: payload.addressee_user_id,
        direct: () => archiveProject(projectId, { reason: payload.reason }),
      }),
    onSuccess: (res) => {
      if (res.mode === 'requested') {
        // Уходить со страницы нельзя: проект НЕ архивирован, он ждёт решения.
        emitToast({
          variant: 'info',
          title: m.approval_request_sent_title(),
          description: m.approval_request_sent_body(),
          durationMs: 6000,
        });
        qc.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
        return;
      }
      emitToast({ variant: 'info', title: m.tm_project_archived(), durationMs: 5000 });
      qc.invalidateQueries({ queryKey: projectKeys.all });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      // Редирект со страниц архивированного проекта.
      void navigate({ to: '/projects' });
    },
  });

  // project-visibility (F4) — смена видимости через member-review диалог (B20).
  const [visibilityTarget, setVisibilityTarget] = useState<'open' | 'private' | null>(null);
  const visibilityMut = useMutation({
    mutationFn: (body: { visibility: 'open' | 'private'; exclude_user_ids: string[] }) =>
      changeProjectVisibility(projectId, body),
    onSuccess: () => {
      setVisibilityTarget(null);
      emitToast({ variant: 'success', title: m.tm_visibility_toast_changed() });
      qc.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      qc.invalidateQueries({ queryKey: ['project', projectId] });
      qc.invalidateQueries({ queryKey: projectKeys.all });
    },
    onError: (err) => {
      emitToast({
        variant: 'error',
        title: err instanceof ApiRequestError ? m.error_by_code(err.code) : m.error_toast_title(),
      });
    },
  });

  const createField = useMutation({
    mutationFn: () =>
      apiRequest(`/task-module/projects/${projectId}/field-definitions`, z.unknown(), {
        method: 'POST',
        body: {
          key: newField.key,
          label: newField.label,
          type: newField.type,
          required: false,
          default_value: null,
          constraints: {},
          template_id: null,
          sequence: (fieldDefs.length + 1) * 10,
        },
      }),
    onSuccess: () => {
      setNewField({ key: '', label: '', type: 'text' });
      qc.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      // FE-27 — явная обратная связь на write (раньше поле добавлялось молча).
      emitToast({ variant: 'success', title: m.tm_field_added() });
    },
    onError: () => emitToast({ variant: 'error', title: m.tm_field_add_failed() }),
  });

  async function deleteField(fieldId: string) {
    await apiRequest(
      `/task-module/projects/${projectId}/field-definitions/${fieldId}`,
      z.unknown(),
      { method: 'DELETE' },
    );
    qc.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
    emitToast({ variant: 'success', title: m.tm_field_deleted() });
  }

  async function setMemberRole(userId: string, role: string) {
    await apiRequest(`/task-module/projects/${projectId}/members/${userId}`, z.unknown(), {
      method: 'PATCH',
      body: { role_in_project: role },
    });
    qc.invalidateQueries({ queryKey: [...projectKeys.detail(projectId), 'members'] });
    // FE-25 — write-действие даёт обратную связь (раньше менялось молча).
    emitToast({ variant: 'success', title: m.tm_role_x({ role: ROLE_LABEL[role] ?? role }) });
  }

  async function removeMember(userId: string) {
    await apiRequest(`/task-module/projects/${projectId}/members/${userId}`, z.unknown(), {
      method: 'DELETE',
    });
    qc.invalidateQueries({ queryKey: [...projectKeys.detail(projectId), 'members'] });
  }

  async function addMember(userId: string) {
    await apiRequest(`/task-module/projects/${projectId}/members`, z.unknown(), {
      method: 'POST',
      body: { user_id: userId, role_in_project: 'member' },
    });
    qc.invalidateQueries({ queryKey: [...projectKeys.detail(projectId), 'members'] });
  }

  // FE-24 — email-инвайт нового участника (BE-6).
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'lead' | 'member' | 'viewer'>('member');
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteBusy, setInviteBusy] = useState(false);

  async function submitInvite() {
    if (!inviteEmail.trim() || inviteBusy) return;
    setInviteBusy(true);
    setInviteError(null);
    try {
      await apiRequest(`/task-module/projects/${projectId}/members/invite`, z.unknown(), {
        method: 'POST',
        body: { email: inviteEmail.trim(), role_in_project: inviteRole },
      });
      emitToast({
        variant: 'success',
        title: m.task_invite_sent_toast({ email: inviteEmail.trim() }),
      });
      setInviteEmail('');
      setInviteOpen(false);
      qc.invalidateQueries({ queryKey: [...projectKeys.detail(projectId), 'members'] });
    } catch (e) {
      // EMAIL_EXISTS и прочие — локализуем по коду (i18n error-path), без freeform.
      const code = e instanceof ApiRequestError ? e.code : 'unknown';
      setInviteError(m.error_by_code(code));
    } finally {
      setInviteBusy(false);
    }
  }

  const nonMembers = users.filter((u) => !members.some((m) => m.user_id === u.id));

  /**
   * CM-F3 (D4.11a) — довести до раздела, а не до страницы.
   *
   * Срабатывание привязано к ref-у самой секции, а не к эффекту на монтировании
   * вида: при заходе по ссылке «в лоб» настройки монтируются ПОЗЖЕ корня (вид
   * приходит из URL через эффект), и эффект, ищущий узел по `id`, гонялся с
   * рендером — на клиентском переходе якорь срабатывал, на перезагрузке нет.
   * Ref вызывается ровно тогда, когда узел появился в DOM, — гонки не остаётся.
   *
   * Подсветка гаснет отдельным эффектом. Собранная вместе с прокруткой, она не
   * гасла вовсе: гвард «прокрутить один раз» съедал и таймер (под StrictMode
   * первый проход ставил таймер, cleanup снимал, повторный уходил в ранний
   * return и нового не заводил).
   */
  const anchoredRef = useRef<string | null>(null);
  const [anchorHit, setAnchorHit] = useState<string | null>(null);
  const attachAnchor = useCallback(
    (node: HTMLElement | null) => {
      if (!node || !section || anchoredRef.current === section) return;
      anchoredRef.current = section;
      node.scrollIntoView({ block: 'start', behavior: 'smooth' });
      setAnchorHit(section);
    },
    [section],
  );
  useEffect(() => {
    if (!anchorHit) return;
    // 4 с, а не «две»: при заходе по ссылке страница ещё догружает состояния,
    // поля и участников — подсветка, гаснущая за время спиннера, не видна никому.
    const timer = window.setTimeout(() => setAnchorHit(null), 4000);
    return () => window.clearTimeout(timer);
  }, [anchorHit]);
  /** Класс + ref якорной секции одним местом — чтобы их нельзя было расцепить. */
  const sectionProps = (
    key: ProjectSettingsSection,
    ...rest: string[]
  ): { id: string; className: string; ref?: (node: HTMLElement | null) => void } => ({
    id: key,
    className: [styles.section, ...rest, anchorHit === key ? styles.sectionAnchored : '']
      .filter(Boolean)
      .join(' '),
    ...(section === key ? { ref: attachAnchor } : {}),
  });

  return (
    <div className={styles.settings}>
      <section {...sectionProps('states', styles.sectionWide)}>
        <h2 className={styles.sectionTitle}>{m.tm_states()}</h2>
        <p className={styles.hint}>{m.tm_states_hint()}</p>
        <StatesManager projectId={projectId} states={states} />
      </section>

      <section {...sectionProps('fields')}>
        <h2 className={styles.sectionTitle}>{m.tm_project_fields()}</h2>
        <ul className={styles.list}>
          {fieldDefs.map((f) => (
            <li key={f.id} className={styles.row}>
              <span className={styles.fieldLabel}>{f.label}</span>
              <code className={styles.fieldKey}>{f.key}</code>
              <span className={styles.muted}>{f.type}</span>
              {f.required && <span className={styles.badge}>{m.tm_required_abbr()}</span>}
              <button
                type="button"
                className={styles.iconBtn}
                onClick={() => deleteField(f.id)}
                aria-label={m.tm_delete_field_x({ label: f.label })}
                title={m.tm_soft_delete_30d()}
              >
                <Trash2 size={13} />
              </button>
            </li>
          ))}
          {fieldDefs.length === 0 && <li className={styles.empty}>{m.tm_no_fields()}</li>}
        </ul>
        <div className={styles.addRow}>
          <input
            className={styles.input}
            placeholder={m.tm_field_key_ph()}
            value={newField.key}
            onChange={(e) => setNewField({ ...newField, key: e.target.value })}
          />
          <input
            className={styles.input}
            placeholder={m.tm_name()}
            value={newField.label}
            onChange={(e) => setNewField({ ...newField, label: e.target.value })}
          />
          <div className={styles.typeRow} role="radiogroup" aria-label={m.tm_field_type()}>
            {FIELD_TYPES.map((t) => (
              <button
                key={t}
                type="button"
                role="radio"
                aria-checked={newField.type === t}
                className={[styles.typeOpt, newField.type === t ? styles.typeActive : ''].join(' ')}
                onClick={() => setNewField({ ...newField, type: t })}
              >
                {FIELD_TYPE_LABEL[t]}
              </button>
            ))}
          </div>
          <button
            type="button"
            className={styles.addBtn}
            disabled={!newField.key || !newField.label || createField.isPending}
            onClick={() => createField.mutate()}
          >
            {m.tm_add_field()}
          </button>
        </div>
      </section>

      {/* project-visibility (F4) — управление видимостью проекта. */}
      <section {...sectionProps('visibility')}>
        <h2 className={styles.sectionTitle}>{m.tm_visibility_section_title()}</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              color: 'var(--text-secondary)',
            }}
          >
            {projectQuery.data?.visibility === 'private' ? (
              <Lock size={14} aria-hidden="true" />
            ) : (
              <Globe size={14} aria-hidden="true" />
            )}
            {projectQuery.data?.visibility === 'private'
              ? m.tm_visibility_current_private()
              : m.tm_visibility_current_open()}
          </span>
          {/* Право менять видимость гейтит BE (lead/admin/company_admin, §2.3):
              неавторизованный клик вернёт локализованную ошибку в тост. */}
          <Button
            size="sm"
            variant="secondary"
            onClick={() =>
              setVisibilityTarget(projectQuery.data?.visibility === 'private' ? 'open' : 'private')
            }
          >
            {projectQuery.data?.visibility === 'private'
              ? m.tm_visibility_make_open()
              : m.tm_visibility_make_private()}
          </Button>
        </div>
      </section>
      {visibilityTarget && (
        <VisibilityChangeDialog
          open
          projectId={projectId}
          target={visibilityTarget}
          pending={visibilityMut.isPending}
          onCancel={() => setVisibilityTarget(null)}
          onConfirm={(excludeUserIds) =>
            visibilityMut.mutate({ visibility: visibilityTarget, exclude_user_ids: excludeUserIds })
          }
        />
      )}

      <section {...sectionProps('members')}>
        <h2 className={styles.sectionTitle}>{m.tm_members()}</h2>
        <ul className={styles.list}>
          {members.map((mem) => {
            // FE-23 — имя из denorm BE-4 (full_name → email → короткий id),
            // больше не зависим от неполного `users`-prop.
            const displayName = mem.full_name ?? mem.email ?? mem.user_id.slice(0, 8);
            const isLastLead =
              mem.role_in_project === 'lead' &&
              members.filter((x) => x.role_in_project === 'lead').length === 1;
            return (
              <li key={mem.user_id} className={styles.row}>
                <span className={styles.avatar}>{displayName.slice(0, 1).toUpperCase()}</span>
                <span className={styles.fieldLabel} title={mem.email ?? undefined}>
                  {displayName}
                </span>
                <div className={styles.typeRow} role="radiogroup" aria-label={m.tm_project_role()}>
                  {Object.entries(ROLE_LABEL).map(([role, label]) => (
                    <button
                      key={role}
                      type="button"
                      role="radio"
                      aria-checked={mem.role_in_project === role}
                      className={[
                        styles.typeOpt,
                        mem.role_in_project === role ? styles.typeActive : '',
                      ].join(' ')}
                      onClick={() => setMemberRole(mem.user_id, role)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className={styles.iconBtn}
                  disabled={isLastLead}
                  title={isLastLead ? m.tm_cant_remove_last_lead() : m.tm_remove_from_project()}
                  onClick={() => removeMember(mem.user_id)}
                  aria-label={m.tm_remove_from_project()}
                >
                  <Trash2 size={13} />
                </button>
              </li>
            );
          })}
        </ul>
        {nonMembers.length > 0 && (
          <div className={styles.addRow}>
            <UserPlus size={14} className={styles.muted} aria-hidden />
            {nonMembers.slice(0, 8).map((u) => (
              <button
                key={u.id}
                type="button"
                className={styles.typeOpt}
                onClick={() => addMember(u.id)}
              >
                + {u.name}
              </button>
            ))}
          </div>
        )}

        {/* FE-24 — пригласить нового участника по email (BE-6). */}
        {inviteOpen ? (
          <div className={styles.inviteForm}>
            <span className={styles.inviteTitle}>{m.task_invite_dialog_title()}</span>
            <label className={styles.inviteLabel} htmlFor="invite-email">
              {m.task_invite_email_label()}
            </label>
            <input
              id="invite-email"
              type="email"
              className={[styles.input, inviteError ? styles.inputError : ''].join(' ')}
              aria-invalid={inviteError ? true : undefined}
              placeholder={m.task_invite_email_placeholder()}
              value={inviteEmail}
              // biome-ignore lint/a11y/noAutofocus: диалог-инвайт сразу принимает email
              autoFocus
              onChange={(e) => {
                setInviteEmail(e.target.value);
                setInviteError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submitInvite();
                if (e.key === 'Escape') setInviteOpen(false);
              }}
            />
            <span className={styles.inviteRoleLabel}>{m.task_invite_role_label()}</span>
            <div
              className={styles.typeRow}
              role="radiogroup"
              aria-label={m.task_invite_role_label()}
            >
              {(['lead', 'member', 'viewer'] as const).map((role) => (
                <button
                  key={role}
                  type="button"
                  role="radio"
                  aria-checked={inviteRole === role}
                  className={[styles.typeOpt, inviteRole === role ? styles.typeActive : ''].join(
                    ' ',
                  )}
                  onClick={() => setInviteRole(role)}
                >
                  {ROLE_LABEL[role]}
                </button>
              ))}
            </div>
            {inviteError && (
              <p className={styles.inviteError} role="alert" aria-live="polite">
                <CircleAlert size={14} aria-hidden />
                {inviteError}
              </p>
            )}
            <div className={styles.inviteActions}>
              <button
                type="button"
                className={styles.addBtn}
                disabled={!inviteEmail.trim() || inviteBusy}
                onClick={submitInvite}
              >
                {m.task_invite_submit()}
              </button>
              <button type="button" className={styles.typeOpt} onClick={() => setInviteOpen(false)}>
                {m.task_invite_cancel()}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className={styles.inviteTrigger}
            onClick={() => {
              setInviteError(null);
              setInviteOpen(true);
            }}
          >
            <Mail size={14} aria-hidden />
            {m.task_invite_button()}
          </button>
        )}
      </section>

      {/* F4.1 — «Сохранить как шаблон» / «Дублировать проект» в workspace (вторая
          точка входа помимо HeaderActions detail-страницы). RBAC — DisabledWithReason. */}
      <section {...sectionProps('template')}>
        <h2 className={styles.sectionTitle}>{m.tmpl_menu_save_as_template()}</h2>
        <div className={styles.dangerActions}>
          <DisabledWithReason ability="project:update" can={canEditProject}>
            <button
              type="button"
              className={styles.typeOpt}
              onClick={() => setTemplateDialog('save')}
            >
              {m.tmpl_menu_save_as_template()}
            </button>
          </DisabledWithReason>
          <DisabledWithReason ability="project:update" can={canEditProject}>
            <button
              type="button"
              className={styles.typeOpt}
              onClick={() => setTemplateDialog('duplicate')}
            >
              {m.tmpl_menu_duplicate()}
            </button>
          </DisabledWithReason>
        </div>
      </section>

      {templateDialog === 'save' && (
        <SaveAsTemplateDialog
          project={{ id: projectId, name: projectName }}
          onClose={() => setTemplateDialog(null)}
        />
      )}
      {templateDialog === 'duplicate' && (
        <DuplicateProjectDialog
          project={{ id: projectId, name: projectName }}
          onClose={() => setTemplateDialog(null)}
        />
      )}

      {/* FB15 (M2M) — архивация проекта: preview судьбы задач (BB11) + confirm.
          Мульти-проектные задачи получают новый главный (со сменой номера),
          эксклюзивные архивируются, вторичные членства снимаются. */}
      <section {...sectionProps('archive', styles.dangerSection)}>
        <h2 className={styles.sectionTitle}>{m.tm_archive_section()}</h2>
        {/**
         * F7 (project-actions-parity §3.2) — секция перестала быть однокликовой.
         *
         * Три правки, каждая по своей находке:
         *  1. Подтверждение уехало в диалог с обоснованием и подписью (Tier 2) —
         *     раньше архивация уходила по одному клику, будучи разрушительнее
         *     отмены, которая требовала подписи.
         *  2. Иконка `Trash2` заменена на `Archive`: корзина обещала удаление
         *     того, что на деле убирается с глаз (и после B10 — обратимо).
         *  3. У триггера появился RBAC-гейт. Его не было ВОВСЕ, в отличие от
         *     соседней секции шаблона: кто дошёл до экрана — тот и архивировал.
         */}
        <DisabledWithReason ability="project:delete" can={canDeleteProject}>
          <button
            type="button"
            className={styles.dangerTrigger}
            onClick={() => setArchiveConfirmOpen(true)}
          >
            <Archive size={14} aria-hidden />
            {m.tm_archive_project_ellipsis()}
          </button>
        </DisabledWithReason>
        <ArchiveProjectDialog
          open={archiveConfirmOpen}
          projectId={projectId}
          projectName={projectName ?? ''}
          projectCode={null}
          onConfirm={(payload) => archiveMutation.mutate(payload)}
          onCancel={() => setArchiveConfirmOpen(false)}
        />
        {/**
         * F9 (project-actions-parity §1.2) — выход из архива стоит рядом со входом.
         * До B10/B12 его не существовало: архивация удаляла членства и связи, и
         * возвращать было физически не из чего.
         */}
        {isArchived && (
          <>
            <DisabledWithReason ability="project:delete" can={canDeleteProject}>
              <button
                type="button"
                className={styles.dangerTrigger}
                onClick={() => setRestoreOpen(true)}
              >
                <ArchiveRestore size={14} aria-hidden />
                {m.ctx_restore_project()}
              </button>
            </DisabledWithReason>
            <RestoreProjectDialog
              open={restoreOpen}
              projectId={projectId}
              projectName={projectName ?? ''}
              onConfirm={(payload) => restoreMutation.mutate(payload)}
              onCancel={() => setRestoreOpen(false)}
            />
          </>
        )}
      </section>
    </div>
  );
}
